import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Save, ExternalLink, Columns3, Table2, Package, Video, FolderOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { api, errMsg, fmtUSD, fmtDate, toDateInput, fromDateInput } from "@/lib/api";
import { DEAL_STAGES, dealStageLabel, INVENTORY, inventoryLabel, INVOICE, SOCIAL_CHANNELS } from "@/lib/constants";
import { PageHeader, Pill, KanbanBoard, Metric, SampleTag, DealStageTag } from "@/components/app/shared";
import { cn } from "@/lib/utils";
import ProductsDialog from "@/components/crm/ProductsDialog";
import RevenueCharts from "@/components/crm/RevenueCharts";
import DealQuotesPanel from "@/components/crm/DealQuotesPanel";
import MeetingDialog from "@/components/crm/MeetingDialog";
import DrivePicker from "@/components/google/DrivePicker";
import TasksWidget from "@/components/google/TasksWidget";
import FirstDealGuide from "@/components/crm/FirstDealGuide";

const TRAFFIC = [
  { id: "assetsReceived", label: "Artes de marca recibidos (Canva / Drive)" },
  { id: "copyCleared", label: "Copy aprobado por el cliente" },
  { id: "scheduled", label: "Fecha de publicación programada" },
  { id: "analyticsReport", label: "Reporte de resultados post-campaña entregado" },
];
const blank = { clientName: "", agency: "", contactName: "", contactEmail: "", dealValue: 0, stage: "LEAD", inventoryType: "SPONSORED_WEB", deadline: null, scheduledDate: null, assetDeliveryUrl: "", invoiceStatus: "NONE", assignedTo: "", notes: "", channels: [], checklist: { assetsReceived: false, copyCleared: false, trackingUrls: "", scheduled: false, analyticsReport: false } };
const trafficDone = (d) => TRAFFIC.filter((t) => d.checklist?.[t.id]).length;

function DealSheet({ deal, setDeal }) {
  const qc = useQueryClient();
  const [f, setF] = useState(null);
  const [meetOpen, setMeetOpen] = useState(false);
  const [driveOpen, setDriveOpen] = useState(false);
  useEffect(() => {
    setF(deal ? JSON.parse(JSON.stringify({ ...blank, ...deal })) : null);
  }, [deal]);
  const isNew = deal && !deal.id;
  const inv = () => { qc.invalidateQueries({ queryKey: ["deals"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); qc.invalidateQueries({ queryKey: ["guide-first-deal"] }); };
  const save = useMutation({
    mutationFn: async () => {
      const { id, createdAt, updatedAt, isSample, ...body } = f;
      body.dealValue = Number(body.dealValue) || 0;
      return isNew ? (await api.post("/deals", body)).data : (await api.patch(`/deals/${deal.id}`, body)).data;
    },
    onSuccess: () => { inv(); toast.success(isNew ? "Lead agregado al pipeline" : "Negocio actualizado"); setDeal(null); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const del = useMutation({
    mutationFn: async () => api.delete(`/deals/${deal.id}`),
    onSuccess: () => { inv(); toast.success("Negocio eliminado"); setDeal(null); },
    onError: (e) => toast.error(errMsg(e)),
  });
  if (!f || !deal) return null;
  const set = (k, v) => setF({ ...f, [k]: v });
  const setCk = (k, v) => setF({ ...f, checklist: { ...f.checklist, [k]: v } });
  return (
    <Sheet open={!!deal} onOpenChange={(o) => !o && setDeal(null)}>
      <SheetContent className="w-full overflow-y-auto bg-card sm:max-w-xl" data-testid="deal-sheet">
        <SheetHeader>
          <div className="kicker">{isNew ? "Nuevo lead" : "Ficha del negocio"}</div>
          <SheetTitle className="font-display text-5xl font-normal">{f.clientName || "Anunciante"}</SheetTitle>
          <SheetDescription>{isNew ? "Registra el anunciante y el producto de interés." : `${dealStageLabel(deal.stage)} · ${fmtUSD(deal.dealValue)}`}</SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 grid gap-1.5"><Label className="text-xs">Anunciante / marca</Label><Input value={f.clientName} onChange={(e) => set("clientName", e.target.value)} data-testid="deal-client-input" /></div>
            <div className="grid gap-1.5"><Label className="text-xs">Agencia</Label><Input value={f.agency} onChange={(e) => set("agency", e.target.value)} data-testid="deal-agency-input" /></div>
            <div className="grid gap-1.5"><Label className="text-xs">Valor (USD)</Label><Input type="number" min="0" value={f.dealValue} onChange={(e) => set("dealValue", e.target.value)} className="font-mono" data-testid="deal-value-input" /></div>
            <div className="grid gap-1.5"><Label className="text-xs">Contacto</Label><Input value={f.contactName} onChange={(e) => set("contactName", e.target.value)} data-testid="deal-contact-input" /></div>
            <div className="grid gap-1.5"><Label className="text-xs">Correo de contacto</Label><Input type="email" value={f.contactEmail} onChange={(e) => set("contactEmail", e.target.value)} data-testid="deal-email-input" /></div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Etapa</Label>
              <Select value={f.stage} onValueChange={(v) => set("stage", v)}>
                <SelectTrigger data-testid="deal-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DEAL_STAGES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Producto</Label>
              <Select value={f.inventoryType} onValueChange={(v) => set("inventoryType", v)}>
                <SelectTrigger data-testid="deal-inventory-select"><SelectValue /></SelectTrigger>
                <SelectContent>{INVENTORY.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {f.inventoryType === "SOCIAL_CAMPAIGN" && (
              <div className="col-span-2 flex flex-wrap gap-2" data-testid="deal-channels">
                {SOCIAL_CHANNELS.map((c) => {
                  const on = f.channels.includes(c);
                  return (
                    <button key={c} type="button" onClick={() => set("channels", on ? f.channels.filter((x) => x !== c) : [...f.channels, c])}
                      className={cn("rounded-full border px-3 py-1 text-xs transition-colors", on ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-accent")}>{c}</button>
                  );
                })}
              </div>
            )}
            <div className="grid gap-1.5"><Label className="text-xs">Entrega / deadline</Label><Input type="date" value={toDateInput(f.deadline)} onChange={(e) => set("deadline", fromDateInput(e.target.value))} data-testid="deal-deadline-input" /></div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Facturación</Label>
              <Select value={f.invoiceStatus} onValueChange={(v) => set("invoiceStatus", v)}>
                <SelectTrigger data-testid="deal-invoice-select"><SelectValue /></SelectTrigger>
                <SelectContent>{INVOICE.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 grid gap-1.5"><Label className="text-xs">Ejecutivo/a asignado</Label><Input value={f.assignedTo} onChange={(e) => set("assignedTo", e.target.value)} data-testid="deal-assigned-input" /></div>
          </div>
          <section className="rounded-md border border-border p-4" data-testid="deal-traffic-checklist">
            <div className="mb-3 flex items-center justify-between">
              <div className="kicker">Tráfico & producción</div>
              <span className="font-mono text-[11px]">{trafficDone(f)}/4</span>
            </div>
            <div className="space-y-3">
              <label className="flex items-center gap-2.5 text-sm"><Checkbox checked={!!f.checklist.assetsReceived} onCheckedChange={(v) => setCk("assetsReceived", !!v)} data-testid="deal-check-assetsReceived" />{TRAFFIC[0].label}</label>
              <div className="flex gap-1.5 pl-6">
                <Input value={f.assetDeliveryUrl} onChange={(e) => set("assetDeliveryUrl", e.target.value)} placeholder="Enlace de Canva o Google Drive" className="h-8 font-mono text-[11px]" data-testid="deal-asset-url-input" />
                <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={() => setDriveOpen(true)} aria-label="Elegir en Drive" data-testid="deal-asset-drive-button"><FolderOpen className="h-3.5 w-3.5" /></Button>
                {/^https?:/.test(f.assetDeliveryUrl || "") && <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" asChild><a href={f.assetDeliveryUrl} target="_blank" rel="noreferrer" aria-label="Abrir"><ExternalLink className="h-3.5 w-3.5" /></a></Button>}
              </div>
              <label className="flex items-center gap-2.5 text-sm"><Checkbox checked={!!f.checklist.copyCleared} onCheckedChange={(v) => setCk("copyCleared", !!v)} data-testid="deal-check-copyCleared" />{TRAFFIC[1].label}</label>
              <div className="grid gap-1.5">
                <Label className="text-xs">URLs de tracking (UTM)</Label>
                <Textarea rows={2} value={f.checklist.trackingUrls || ""} onChange={(e) => setCk("trackingUrls", e.target.value)} className="font-mono text-[11px]" placeholder="https://medianamag.com/...?utm_source=..." data-testid="deal-tracking-input" />
              </div>
              <label className="flex items-center gap-2.5 text-sm"><Checkbox checked={!!f.checklist.scheduled} onCheckedChange={(v) => setCk("scheduled", !!v)} data-testid="deal-check-scheduled" />{TRAFFIC[2].label}</label>
              <div className="pl-6"><Input type="date" value={toDateInput(f.scheduledDate)} onChange={(e) => set("scheduledDate", fromDateInput(e.target.value))} className="h-8 w-[180px]" data-testid="deal-scheduled-input" /></div>
              <label className="flex items-center gap-2.5 text-sm"><Checkbox checked={!!f.checklist.analyticsReport} onCheckedChange={(v) => setCk("analyticsReport", !!v)} data-testid="deal-check-analyticsReport" />{TRAFFIC[3].label}</label>
            </div>
          </section>
          <div className="grid gap-1.5"><Label className="text-xs">Notas</Label><Textarea rows={3} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="deal-notes-input" /></div>

          {!isNew && (
            <>
              <Separator className="my-2" />
              <div className="flex flex-wrap gap-2" data-testid="deal-google-actions">
                <Button variant="outline" size="sm" onClick={() => setMeetOpen(true)} data-testid="deal-schedule-meet-button">
                  <Video className="mr-1.5 h-3.5 w-3.5" /> Agendar en Meet
                </Button>
              </div>
              <TasksWidget entity="deal" entityId={deal.id} title="Tareas del deal" />
              <DealQuotesPanel deal={deal} />
            </>
          )}

          <div className="flex gap-2">
            {!isNew && <Button variant="outline" size="icon" onClick={() => del.mutate()} disabled={del.isPending} data-testid="deal-delete-button" aria-label="Eliminar"><Trash2 className="h-4 w-4" /></Button>}
            <Button className="flex-1" onClick={() => save.mutate()} disabled={!f.clientName.trim() || save.isPending} data-testid="deal-drawer-save-button">
              {save.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} {isNew ? "Agregar lead" : "Guardar negocio"}
            </Button>
          </div>
        </div>
      </SheetContent>
      <MeetingDialog open={meetOpen} onOpenChange={setMeetOpen} deal={deal} />
      <DrivePicker open={driveOpen} onOpenChange={setDriveOpen} title="Elegir artes en Google Drive" onPick={(file) => set("assetDeliveryUrl", file.webViewLink || "")} />
    </Sheet>
  );
}

const DealCard = ({ d, onClick }) => (
  <div onClick={onClick} data-testid="deal-card" className="cursor-pointer rounded-md border border-border bg-card p-3 transition-colors hover:border-foreground/40">
    <div className="flex items-start justify-between gap-2">
      <div className="text-[13.5px] font-medium leading-snug">{d.clientName}</div>
      {d.isSample && <SampleTag />}
    </div>
    <div className="mt-0.5 text-[11px] text-muted-foreground">{d.agency || "Directo"} · {d.contactName}</div>
    <div className="mt-2.5 font-display text-3xl leading-none">{fmtUSD(d.dealValue)}</div>
    <div className="mt-2 flex items-center justify-between gap-2">
      <Pill tone="peach" className="truncate text-[10px]">{inventoryLabel(d.inventoryType)}</Pill>
      <span className="font-mono text-[10px] text-muted-foreground">{trafficDone(d)}/4 · {fmtDate(d.deadline)}</span>
    </div>
  </div>
);

export default function CRM() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [deal, setDeal] = useState(null);
  const [view, setView] = useState("kanban");
  const [productsOpen, setProductsOpen] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["deals"], queryFn: async () => (await api.get("/deals")).data });
  const deals = data?.deals || [];

  useEffect(() => {
    if (params.get("new") === "1") { setDeal({ ...blank }); params.delete("new"); setParams(params, { replace: true }); }
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    const id = params.get("deal");
    if (id && deals.length) { const d = deals.find((x) => x.id === id); if (d) setDeal(d); params.delete("deal"); setParams(params, { replace: true }); }
    // eslint-disable-next-line
  }, [deals.length]);

  const move = useMutation({
    mutationFn: async ({ id, stage }) => (await api.patch(`/deals/${id}`, { stage })).data,
    onMutate: async ({ id, stage }) => {
      const prev = qc.getQueryData(["deals"]);
      qc.setQueryData(["deals"], (old) => ({ ...old, deals: old.deals.map((d) => (d.id === id ? { ...d, stage } : d)) }));
      return { prev };
    },
    onError: (e, _v, ctx) => { qc.setQueryData(["deals"], ctx.prev); toast.error(errMsg(e)); },
    onSuccess: (d) => toast.success(`${d.clientName} → ${dealStageLabel(d.stage)}`),
    onSettled: () => { qc.invalidateQueries({ queryKey: ["deals"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); },
  });

  const sum = (stages) => deals.filter((d) => stages.includes(d.stage)).reduce((a, d) => a + (d.dealValue || 0), 0);
  const closed = deals.filter((d) => ["WON", "PAID", "LOST"].includes(d.stage));
  const winRate = closed.length ? Math.round((closed.filter((d) => d.stage !== "LOST").length * 100) / closed.length) : 0;

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="05 · CRM Comercial & tráfico de pauta"
        title="CRM Comercial"
        subtitle="Pipeline de anunciantes: historias patrocinadas, campañas en redes, co-host de eventos y pauta en el Libro Anual."
        actions={<>
          <Button variant="outline" onClick={() => { setProductsOpen(true); api.post("/guide/first-deal", { action: "catalog_seen" }).then(() => qc.invalidateQueries({ queryKey: ["guide-first-deal"] })).catch(() => {}); }} data-testid="open-products-button"><Package className="mr-1.5 h-4 w-4" /> Catálogo</Button>
          <Button onClick={() => setDeal({ ...blank })} data-testid="new-deal-button"><Plus className="mr-1.5 h-4 w-4" /> Agregar lead</Button>
        </>}
      />
      <FirstDealGuide
        onOpenCatalog={() => setProductsOpen(true)}
        onNewDeal={() => setDeal({ ...blank })}
        onOpenDeal={(id) => {
          const d = deals.find((x) => x.id === id);
          if (d) setDeal(d);
          else toast.error("No encontramos ese deal; recarga la página.");
        }}
      />
      <RevenueCharts />
      <div className="grid gap-4 md:grid-cols-4">
        <Metric testid="crm-open" label="Pipeline abierto" value={fmtUSD(sum(["LEAD", "DISCOVERY", "PROPOSAL", "NEGOTIATION"]))} hint="Lead a negociación" />
        <Metric testid="crm-booked" label="Reservado" value={fmtUSD(sum(["WON"]))} hint="Ganado, por facturar" />
        <Metric testid="crm-paid" label="Facturado / pagado" value={fmtUSD(sum(["PAID"]))} />
        <Metric testid="crm-winrate" label="Tasa de cierre" value={`${winRate}%`} hint={`${closed.length} negocios cerrados`} />
      </div>
      <Tabs value={view} onValueChange={setView}>
        <TabsList>
          <TabsTrigger value="kanban" data-testid="crm-view-kanban"><Columns3 className="mr-1.5 h-3.5 w-3.5" />Embudo</TabsTrigger>
          <TabsTrigger value="table" data-testid="crm-view-table"><Table2 className="mr-1.5 h-3.5 w-3.5" />Tabla</TabsTrigger>
        </TabsList>
      </Tabs>
      {isLoading ? (
        <div className="flex gap-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-96 w-[296px]" />)}</div>
      ) : view === "kanban" ? (
        <KanbanBoard
          columns={DEAL_STAGES}
          items={deals}
          getColumn={(d) => d.stage}
          onMove={(d, stage) => move.mutate({ id: d.id, stage })}
          renderCard={(d) => <DealCard d={d} onClick={() => setDeal(d)} />}
          columnFooter={(col) => <div className="border-b border-border px-3 py-1.5 font-mono text-[11px] text-muted-foreground">{fmtUSD(data?.totals?.[col.id])}</div>}
        />
      ) : (
        <div className="rounded-lg border border-border bg-card" data-testid="crm-table">
          <Table>
            <TableHeader>
              <TableRow>{["Anunciante", "Agencia", "Producto", "Etapa", "Valor", "Deadline", "Tráfico"].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider">{h}</TableHead>)}</TableRow>
            </TableHeader>
            <TableBody>
              {deals.map((d) => (
                <TableRow key={d.id} className="cursor-pointer" onClick={() => setDeal(d)} data-testid="crm-table-row">
                  <TableCell className="text-sm font-medium">{d.clientName}</TableCell>
                  <TableCell className="text-sm">{d.agency}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{inventoryLabel(d.inventoryType)}</TableCell>
                  <TableCell><DealStageTag stage={d.stage} label={dealStageLabel(d.stage)} /></TableCell>
                  <TableCell className="font-mono text-sm">{fmtUSD(d.dealValue)}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtDate(d.deadline)}</TableCell>
                  <TableCell className="font-mono text-xs">{trafficDone(d)}/4</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <DealSheet deal={deal} setDeal={setDeal} />
      <ProductsDialog open={productsOpen} onOpenChange={setProductsOpen} />
    </div>
  );
}
